import { useState } from "react";

import { type Meta } from "../meta";


interface Props
{
    iniziali: Meta[];
    chiudi: () => void;
    applica: (meta: Meta[]) => void;
}


// Quello che md2pdf sa davvero usare. Elencarle TUTTE, anche vuote, e' la
// differenza fra sapere cosa puoi mettere e doverlo indovinare: un campo vuoto
// e' un invito, una chiave assente e' un segreto.
const NOTE: { chiave: string; cosaFa: string }[] = [
    { chiave: "title", cosaFa: "titolo in copertina, testatina e proprietà del PDF" },
    { chiave: "subtitle", cosaFa: "sottotitolo in copertina" },
    { chiave: "author", cosaFa: "autore in copertina e proprietà del PDF" },
    { chiave: "date", cosaFa: "data in copertina (se vuota: oggi)" },
    { chiave: "edition", cosaFa: "edizione, sotto il titolo" },
    { chiave: "runningHead", cosaFa: "testatina delle pagine (se vuota: il titolo)" },
    { chiave: "keywords", cosaFa: "parole chiave nelle proprietà del PDF" },
];


function tutteLeVoci(iniziali: Meta[]): Meta[]
{
    const presenti = new Map(iniziali.map((m) => [m.chiave, m.valore]));

    const note = NOTE.map((n) => ({ chiave: n.chiave, valore: presenti.get(n.chiave) ?? "" }));

    // Le direttive che il documento ha e questa app non conosce restano: non
    // e' detto che siano un errore, e cancellarle di soppiatto lo sarebbe.
    const altre = iniziali.filter((m) => !NOTE.some((n) => n.chiave === m.chiave));

    return [...note, ...altre];
}


export function Metadati({ iniziali, chiudi, applica }: Props): React.JSX.Element
{
    const [voci, setVoci] = useState<Meta[]>(() => tutteLeVoci(iniziali));

    const cambia = (i: number, campo: keyof Meta, valore: string): void =>
        setVoci(voci.map((v, n) => (n === i ? { ...v, [campo]: valore } : v)));

    // Si scrivono solo le voci con un valore: una chiave vuota non e' un
    // metadato, e svuotare un campo e' il modo di togliere la direttiva.
    const daScrivere = (): Meta[] =>
    {
        const viste = new Set<string>();

        return voci
            .map((v) => ({ chiave: v.chiave.trim(), valore: v.valore.trim() }))
            .filter((v) => v.chiave !== "" && v.valore !== "" && !viste.has(v.chiave) && viste.add(v.chiave) !== undefined);
    };

    return (
        <div className="velo" onMouseDown={(e) => { if (e.target === e.currentTarget) { chiudi(); } }}>
            <div className="finestra" role="dialog" aria-label="Metadati del documento">
                <h2>Metadati</h2>
                <p className="fioco">
                    Tutte le direttive <code>META</code> che md2pdf usa. Quelle vuote non
                    vengono scritte nel documento; svuotare una compilata la toglie.
                </p>

                <div className="tabella">
                    {voci.map((v, i) => (
                        <div className="voce" key={i}>
                            <input
                                aria-label={`chiave ${i + 1}`}
                                className="chiave"
                                value={v.chiave}
                                spellCheck={false}
                                readOnly={i < NOTE.length}
                                onChange={(e) => cambia(i, "chiave", e.target.value)}
                            />
                            <input
                                aria-label={`valore ${i + 1}`}
                                placeholder={NOTE[i]?.cosaFa ?? "valore"}
                                title={NOTE[i]?.cosaFa}
                                value={v.valore}
                                onChange={(e) => cambia(i, "valore", e.target.value)}
                            />
                            <button
                                type="button"
                                aria-label={`svuota ${v.chiave || i + 1}`}
                                title={i < NOTE.length ? "Svuota" : "Togli"}
                                onClick={() => setVoci(i < NOTE.length
                                    ? voci.map((u, n) => (n === i ? { ...u, valore: "" } : u))
                                    : voci.filter((_u, n) => n !== i))}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>

                <div className="azioni">
                    <button type="button" onClick={() => setVoci([...voci, { chiave: "", valore: "" }])}>
                        Aggiungi direttiva
                    </button>
                    <span className="spazio" />
                    <button type="button" onClick={chiudi}>Annulla</button>
                    <button type="button" className="primario" onClick={() => applica(daScrivere())}>
                        Applica al documento
                    </button>
                </div>
            </div>
        </div>
    );
}
